<script>
import IconArrow from '@/components/icon/IconArrow.vue';

export default {
    components: {
        IconArrow,
    },
    computed: {
        sentEmails() {
            return this.$store.getters.SENT_EMAILS;
        },
    },
    data() {
        return {
            expandedEmails: new Set(),
        };
    },
    methods: {
        toggleExpanded(emailId) {
            if (this.expandedEmails.has(emailId)) {
                this.expandedEmails.delete(emailId);
            } else {
                this.expandedEmails.add(emailId);
            }
        },
        isExpanded(emailId) {
            return this.expandedEmails.has(emailId);
        },
        formatTimestamp(timestamp) {
            if (!timestamp) return 'No date';
            const date = new Date(timestamp);
            return isNaN(date.getTime())
                ? 'Invalid date'
                : date.toLocaleString();
        },
    },
};
</script>

<template>
    <div class="emails_list">
        <div v-if="!sentEmails || sentEmails.length === 0" class="no_emails">
            <p>No test emails found</p>
        </div>

        <div v-for="email in sentEmails" :key="email.id" class="email_item">
            <div class="email_header" @click="toggleExpanded(email.id)">
                <div class="email_basic_info">
                    <span class="email_to">{{
                        email.to_address || email.to || 'No recipient'
                    }}</span>
                    <span class="email_subject">{{
                        email.subject || 'No subject'
                    }}</span>
                    <span class="email_timestamp">{{
                        formatTimestamp(email.timestamp)
                    }}</span>
                </div>
                <div class="expand_icon">
                    <IconArrow :class="{ rotated: isExpanded(email.id) }" />
                </div>
            </div>

            <div v-if="isExpanded(email.id)" class="email_content">
                <div class="email_metadata">
                    <div class="metadata_item">
                        <strong>From:</strong>
                        {{ email.from_address || email.from || 'No sender' }}
                    </div>
                    <div class="metadata_item">
                        <strong>Status:</strong> {{ email.status || 'Unknown' }}
                    </div>
                    <div class="metadata_item">
                        <strong>Content Type:</strong>
                        {{ email.content_type || 'Unknown' }}
                    </div>
                    <div class="metadata_item" v-if="email.cc">
                        <strong>CC:</strong> {{ email.cc }}
                    </div>
                    <div class="metadata_item" v-if="email.bcc">
                        <strong>BCC:</strong> {{ email.bcc }}
                    </div>
                </div>

                <div class="email_body">
                    <div
                        v-if="email.content && email.content_type !== 'text'"
                        class="html_content"
                    >
                        <div class="content_label">
                            {{ email.content_type === 'html' ? 'HTML Content:' : 'Email Content:' }}
                        </div>
                        <!-- sandbox="" = no scripts, opaque origin: email HTML can't reach admin tokens -->
                        <iframe class="html_preview" sandbox="" :srcdoc="email.content"></iframe>
                    </div>

                    <div
                        v-if="email.content && email.content_type === 'text'"
                        class="text_content"
                    >
                        <div class="content_label">Text Content:</div>
                        <pre class="text_preview">{{ email.content }}</pre>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.emails_list {
    padding: 20px;
}

.no_emails {
    text-align: center;
    color: var(--text-color-secondary);
    padding: 40px;
}

.email_item {
    border: 1px solid var(--border-color);
    border-radius: 4px;
    margin-bottom: 10px;
    overflow: hidden;
}

.email_header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    cursor: pointer;
    background-color: var(--background-color-light);
    transition: background-color 0.2s;
}

.email_header:hover {
    background-color: var(--background-color-hover);
}

.email_basic_info {
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.email_to {
    font-weight: bold;
    color: var(--text-color-primary);
}

.email_subject {
    color: var(--text-color-primary);
    font-size: 14px;
}

.email_timestamp {
    font-size: 12px;
    color: var(--text-color-secondary);
}

.expand_icon {
    transition: transform 0.2s;
}

.expand_icon.rotated {
    transform: rotate(180deg);
}

.email_content {
    padding: 20px;
    border-top: 1px solid var(--border-color);
    background-color: var(--background-color);
}

.email_metadata {
    margin-bottom: 20px;
}

.metadata_item {
    margin-bottom: 5px;
    font-size: 14px;
}

.content_label {
    font-weight: bold;
    margin-bottom: 10px;
    color: var(--text-color-primary);
}

.html_preview {
    border: 1px solid var(--border-color);
    padding: 15px;
    background-color: white;
    width: 100%;
    height: 400px;
    box-sizing: border-box;
}

.text_preview {
    border: 1px solid var(--border-color);
    padding: 15px;
    background-color: var(--background-color-light);
    white-space: pre-wrap;
    font-family: monospace;
    max-height: 400px;
    overflow-y: auto;
    margin: 0;
}

.html_content,
.text_content {
    margin-bottom: 20px;
}

.html_content:last-child,
.text_content:last-child {
    margin-bottom: 0;
}
</style>
